// ============================================
// FILE: /frontend/src/pages/WhatsAppAnalyticsPage.jsx
// PURPOSE: WhatsApp sharing performance analytics dashboard
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Loader2, 
  Share2, 
  MousePointer, 
  MessageCircle,
  TrendingUp,
  ArrowLeft,
  AlertCircle,
  ExternalLink,
  Calendar,
  ChevronDown,
  X
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { whatsappAPI } from '../api/whatsapp.api';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';

export default function WhatsAppAnalyticsPage() {
  console.log('[WhatsAppAnalyticsPage] 🎨 Component mounted');

  // ========================================
  // STATE & HOOKS
  // ========================================
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(true);
  const [isTopListingsExpanded, setIsTopListingsExpanded] = useState(true);
  const [isActivityExpanded, setIsActivityExpanded] = useState(false);

  // ========================================
  // FETCH ANALYTICS DATA
  // ========================================
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user) return;

      console.log('[WhatsAppAnalyticsPage] 🔵 Fetching analytics...');
      setLoading(true);

      try {
        const response = await whatsappAPI.getAnalytics();
        console.log('[WhatsAppAnalyticsPage] ✅ Analytics received:', response.analytics);
        setAnalytics(response.analytics);

      } catch (error) {
        console.error('[WhatsAppAnalyticsPage] ❌ Fetch error:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to load analytics'
        });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchAnalytics();
    }
  }, [user]);

  // ========================================
  // RENDER: LOADING STATE
  // ========================================
  if (authLoading || loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <div className="container py-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ========================================
  // RENDER: NOT AUTHENTICATED
  // ========================================
  if (!user) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <div className="container py-12">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs sm:text-sm">
                Please <a href="/login" className="underline font-medium">login</a> to view analytics.
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ========================================
  // RENDER: NO DATA STATE
  // ========================================
  if (!analytics || analytics.totalShares === 0) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <div className="container py-6 sm:py-8 md:py-12 max-w-5xl">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="mb-3 sm:mb-4 text-xs sm:text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              Back to Dashboard
            </Button>

            <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-1 sm:mb-2">
              WhatsApp Analytics
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mb-6">
              Track your WhatsApp sharing performance
            </p>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs sm:text-sm">
                You haven't shared any listings on WhatsApp yet. 
                <a href="/dashboard/whatsapp-tools" className="underline ml-1 font-medium">Start sharing</a> to see analytics.
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { totalShares, totalClicks, totalChats, clickThroughRate, chatConversionRate, topListings, recentActivity } = analytics;

  console.log('[WhatsAppAnalyticsPage] 📊 Rendering analytics:', {
    totalShares,
    totalClicks,
    topListingsCount: topListings?.length,
    recentActivityCount: recentActivity?.length
  });

  // ========================================
  // RENDER: MAIN CONTENT
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow bg-muted/30">
        <div className="container py-6 sm:py-8 md:py-12 max-w-5xl">
          
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="mb-3 sm:mb-4 text-xs sm:text-sm font-medium hover:bg-accent"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              Back to Dashboard
            </Button>

            <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-1 sm:mb-2">
              WhatsApp Analytics
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Track your WhatsApp sharing performance
            </p>
          </div>

          {/* Performance Overview Card */}
          <Card className="mb-6 sm:mb-8 shadow-sm">
            <CardHeader 
              className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm sm:text-base md:text-lg">
                  Performance Overview
                </CardTitle>
                {isOverviewExpanded ? (
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                )}
              </div>
            </CardHeader>

            {isOverviewExpanded && (
              <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                  
                  {/* Total Shares Stat */}
                  <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                      <p className="text-[10px] sm:text-xs font-medium text-blue-900">
                        Total Shares
                      </p>
                    </div>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900">
                      {totalShares}
                    </p>
                  </div>

                  {/* Total Clicks Stat */}
                  <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <MousePointer className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                      <p className="text-[10px] sm:text-xs font-medium text-green-900">
                        Total Clicks
                      </p>
                    </div>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold text-green-900">
                      {totalClicks}
                    </p>
                  </div>

                  {/* Total Chats Stat */}
                  <div className="bg-purple-50 p-3 sm:p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
                      <p className="text-[10px] sm:text-xs font-medium text-purple-900">
                        Total Chats
                      </p>
                    </div>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold text-purple-900">
                      {totalChats}
                    </p>
                  </div>

                  {/* Click Rate Stat */}
                  <div className="bg-orange-50 p-3 sm:p-4 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 flex-shrink-0" />
                      <p className="text-[10px] sm:text-xs font-medium text-orange-900">
                        Click Rate
                      </p>
                    </div>
                    <p className="text-lg sm:text-xl md:text-2xl font-bold text-orange-900">
                      {clickThroughRate}%
                    </p>
                  </div>
                </div>

                {/* Chat Conversion Rate */}
                <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm font-medium mb-0.5 text-primary">
                        Chat Conversion Rate
                      </p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        Clicks that led to WhatsApp chats
                      </p>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-primary">
                      {chatConversionRate}%
                    </p>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Top Performing Listings Card */}
          <Card className="mb-6 sm:mb-8 shadow-sm">
            <CardHeader 
              className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => setIsTopListingsExpanded(!isTopListingsExpanded)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm sm:text-base md:text-lg">
                  Top Performing Listings
                </CardTitle>
                {isTopListingsExpanded ? (
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                )}
              </div>
            </CardHeader>

            {isTopListingsExpanded && (
              <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                <div className="space-y-2 sm:space-y-3">
                  {topListings && topListings.length > 0 ? (
                    topListings.map((item, index) => (
                      <div
                        key={item.listing.id}
                        className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                      >
                        {/* Rank Badge */}
                        <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] sm:text-xs font-bold">
                          {index + 1}
                        </div>

                        {/* Listing Details */}
                        <div className="flex-grow min-w-0">
                          <h3 className="text-xs sm:text-sm font-medium line-clamp-1 mb-0.5">
                            {item.listing.title}
                          </h3>
                          <p className="text-xs sm:text-sm font-bold text-primary mb-1 sm:mb-1.5">
                            KES {parseFloat(item.listing.price).toLocaleString()}
                          </p>
                          
                          {/* Performance Stats */}
                          <div className="flex flex-wrap gap-2 sm:gap-3">
                            <div className="flex items-center gap-1">
                              <Share2 className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                              <span className="text-[10px] sm:text-xs text-muted-foreground">
                                {item.shares} shares
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MousePointer className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                              <span className="text-[10px] sm:text-xs text-muted-foreground">
                                {item.clicks} clicks
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500" />
                              <span className="text-[10px] sm:text-xs text-muted-foreground">
                                {item.chats} chats
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* View Listing Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="flex-shrink-0 h-8 w-8 sm:h-9 sm:w-9"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/listing/${item.listing.id}`);
                          }}
                        >
                          <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs sm:text-sm text-muted-foreground text-center py-6">
                      No listings shared yet
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Recent Activity Card */}
          <Card className="shadow-sm">
            <CardHeader 
              className="p-3 sm:p-4 md:p-6 cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => setIsActivityExpanded(!isActivityExpanded)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm sm:text-base md:text-lg">
                  Recent Activity
                </CardTitle>
                {isActivityExpanded ? (
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                )}
              </div>
            </CardHeader>

            {isActivityExpanded && (
              <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
                <div className="space-y-2 sm:space-y-3">
                  {recentActivity && recentActivity.length > 0 ? (
                    recentActivity.map((activity) => (
                      <div
                        key={activity.id}
                        className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border bg-card"
                      >
                        {/* Activity Type Icon */}
                        <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${
                          activity.shareType === 'bulk' 
                            ? 'bg-blue-100 text-blue-600' 
                            : activity.shareType === 'chat'
                            ? 'bg-purple-100 text-purple-600'
                            : 'bg-green-100 text-green-600'
                        }`}>
                          {activity.shareType === 'chat' ? (
                            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                          ) : (
                            <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                          )}
                        </div>

                        {/* Activity Details */}
                        <div className="flex-grow min-w-0">
                          <h4 className="text-xs sm:text-sm font-medium line-clamp-1 mb-0.5">
                            {activity.listing.title}
                          </h4>
                          <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground mb-1">
                            <Badge variant="outline" className="text-[10px] sm:text-xs capitalize">
                              {activity.shareType}
                            </Badge>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(activity.lastSharedAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground">
                            <span>{activity.shareCount} shares</span>
                            <span>•</span>
                            <span>{activity.clickCount} clicks</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs sm:text-sm text-muted-foreground text-center py-6">
                      No recent activity
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}